import { useNavigate } from 'react-router-dom';

import { WelcomeTemplateEntity } from '../../../../../types';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { useQueryClient } from '@tanstack/react-query';
import { UserKeys } from '../../../../api/users-api';
import { useMemo, useState } from 'react';
import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import WelcomeTemplateDetailsDialog from './welcome-template-details-dialog';
import WelcomeTemplateDetailsDrawer from './welcome-template-details-drawer';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import DeleteWelcomeTemplateDialog from './delete-welcome-template-dialog';
import { Messages } from '../../../../utils/message-constants';
import { createGridOverlays } from '../../../grid-overlay/no-data-overlay-helpers';
import { pagesConfig } from '../../../../shared/pages-config';
import SharedDataGridClient from '../../../../shared/shared-data-grid-client';
import SharedToolbar from '../../../../shared/shared-toolbar-latest';
import CreateButton from '../../../../shared/buttons/create-button';
import SharedIconButton from '../../../../shared/buttons/shared-icon-button';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { isFeatureEnabled } from '../../../../utils/feature-flags';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Edit from '@mui/icons-material/Edit';
import Feed from '@mui/icons-material/Feed';

interface WelcomeTemplatesGridProps {
  templates: WelcomeTemplateEntity[];
  isLoading: boolean;
  isFetching: boolean;
  changeLogRoles: string[];
  administrationRoles: string[];
}

export default function WelcomeTemplatesGrid({
  templates,
  isLoading,
  isFetching,
  changeLogRoles,
  administrationRoles,
}: WelcomeTemplatesGridProps) {
  const { createEvent } = useAnalytics();
  const { features } = useUserSettings();
  const uiEnhancementEnabled = isFeatureEnabled(
    'uiEnhancementPhase2',
    features
  );
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [openWelcomeTemplateDetails, setOpenWelcomeTemplateDetails] =
    useState(false);
  const [selectedWelcomeTemplate, setSelectedWelcomeTemplate] =
    useState<WelcomeTemplateEntity>();
  const [openDeleteWelcomeTemplates, setOpenDeleteWelcomeTemplates] =
    useState(false);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const selectedWelcomeTemplates = templates.filter((t) =>
    selectedRows.includes(t.id)
  );
  const hasReadonlySelection = useMemo(
    () => selectedWelcomeTemplates.some((template) => template.readonly),
    [selectedWelcomeTemplates]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'createdDate',
        headerName: 'Created On',
        flex: 1,
        display: 'flex',
        type: 'dateTime',
        valueGetter: (value) => {
          const date = new Date(value);
          return date;
        },
        valueFormatter: (params: Date) => {
          const formattedDate = dayjs(params).format('MMM D, YYYY, h:mm A');
          return formattedDate;
        },
      },
      {
        field: 'templateName',
        headerName: 'Template Name',
        flex: 1,
        display: 'flex',
      },
      {
        field: 'default',
        headerName: 'Default',
        flex: 0.5,
        display: 'flex',
        type: 'boolean',
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 2,
        display: 'flex',
        valueGetter: (value: string) => {
          return value;
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        type: 'actions',
        getActions: (params) => {
          const buttonArray = [];
          if (
            isAbleToSeeUIElement(
              administrationRoles,
              AdministrationRoles.EditWelcomeTemplate.value
            )
          ) {
            buttonArray.push(
              <GridActionsCellItem
                label={'Edit'}
                icon={<Edit />}
                onClick={() => {
                  navigate(
                    `/${pagesConfig.settings.link}/${
                      pagesConfig.welcomeTemplates.link
                    }/${pagesConfig.editWelcomeTemplatesFormPage.link.replace(
                      ':id',
                      params.row.id.toString()
                    )}`
                  );
                }}
              />
            );
          }
          return buttonArray;
        },
      },
    ],
    [administrationRoles, navigate]
  );

  function CustomToolbar() {
    return (
      <SharedToolbar
        noColumn
        noFilter
        primaryButtons={
          <CreateButton
            onClick={() => {
              navigate(
                `/${pagesConfig.settings.link}/${pagesConfig.welcomeTemplates.link}/${pagesConfig.welcomeTemplatesFormPage.link}`
              );
            }}
            disabled={
              !isAbleToSeeUIElement(
                administrationRoles,
                AdministrationRoles.CreateWelcomeTemplates.value
              )
            }
            label="Create Template"
          />
        }
        secondaryButtons={
          <>
            <SharedIconButton
              variant="delete"
              onClick={() => {
                setOpenDeleteWelcomeTemplates(true);
              }}
              disabled={
                selectedRows.length === 0 ||
                !isAbleToSeeUIElement(
                  administrationRoles || [],
                  AdministrationRoles.DeleteWelcomeTemplate.value
                ) ||
                hasReadonlySelection
              }
              tooltipLabel={
                selectedRows.length === 0
                  ? 'Select templates to delete'
                  : hasReadonlySelection
                  ? 'gPanel Default Welcome Template cannot be deleted.'
                  : !isAbleToSeeUIElement(
                      administrationRoles,
                      AdministrationRoles.DeleteWelcomeTemplate.value
                    )
                  ? Messages.Errors.Permission.Acting(
                      AdministrationRoles.DeleteWelcomeTemplate.name
                    )
                  : 'Delete'
              }
            />
            <SharedIconButton
              variant="refresh"
              onClick={() => {
                queryClient.invalidateQueries({
                  queryKey: [UserKeys.EmailTemplates],
                });
              }}
              tooltipLabel="Refresh"
            />
          </>
        }
      />
    );
  }

  return (
    <Box sx={{ px: 1 }}>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Manage and view email templates that are available to send when creating
        a user in gPanel.
      </Typography>
      <Box sx={{ height: '56vh', mx: 'auto' }}>
        <SharedDataGridClient
          slots={{
            toolbar: CustomToolbar,
            ...createGridOverlays('Welcome Templates', <Feed />),
          }}
          rows={templates || []}
          columns={columns}
          checkboxSelection
          disableRowSelectionOnClick
          loading={isLoading || isFetching}
          defaultGridState={{
            sorting: {
              sortModel: [
                {
                  field: 'createdDate',
                  sort: 'desc',
                },
              ],
            },
          }}
          onRowClick={(row) => {
            setSelectedWelcomeTemplate(row.row);
            setOpenWelcomeTemplateDetails(true);
            createEvent(
              analytics.administration.settings.view_welcome_template_details,
              {
                module: ModuleList.modules.administration.name,
                section: ModuleList.modules.administration.sections.settings,
              }
            );
          }}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          isRowSelectable={(params) => !params.row.readonly}
        />

        {selectedWelcomeTemplate ? (
          uiEnhancementEnabled ? (
            <WelcomeTemplateDetailsDrawer
              open={openWelcomeTemplateDetails}
              handleClose={() => setOpenWelcomeTemplateDetails(false)}
              selectedWelcomeTemplate={selectedWelcomeTemplate}
            />
          ) : (
            <WelcomeTemplateDetailsDialog
              open={openWelcomeTemplateDetails}
              handleClose={() => setOpenWelcomeTemplateDetails(false)}
              selectedWelcomeTemplate={selectedWelcomeTemplate}
            />
          )
        ) : null}

        {selectedRows.length > 0 && openDeleteWelcomeTemplates ? (
          <DeleteWelcomeTemplateDialog
            open={openDeleteWelcomeTemplates}
            handleClose={() => {
              setOpenDeleteWelcomeTemplates(false);
            }}
            selectedWelcomeTemplates={selectedWelcomeTemplates}
            changeLogRoles={changeLogRoles}
          />
        ) : null}
      </Box>
    </Box>
  );
}
